import { z } from "zod";
import { SystemEntity, SYSTEM_ENTITY_LABELS } from "@erp/shared-types";
import type { SocketType } from "../../types";
import { generateSocketsFromSchema } from "../socket-type-inference";
import { createTaskStep } from "../../steps/tasks/create-task.step";
import { createTypedTaskStep } from "../../steps/tasks/create-typed-task.step";
import { generateTaskStep } from "../../steps/tasks/generate-task.step";
import { createSubtaskStep } from "../../steps/tasks/create-subtask.step";
import type { DynamicSocketDefinition } from "./socketDefinition";

/**
 * Create-family steps whose source/anchor input sockets are rewritten from
 * the selected task type. `taskTypeAnchorEntityType` is copied onto step
 * config when a type with an anchor is picked (sync-dynamic — no DB lookup
 * at socket-generation time).
 */
export const TASK_CREATE_STEP_TYPES = [
  "create_task",
  "create_typed_task",
  "generate_task",
  "create_subtask",
] as const;

export type TaskCreateStepType = (typeof TASK_CREATE_STEP_TYPES)[number];

/** Config key holding the selected type's declared `anchorEntityType`. */
export const TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY =
  "taskTypeAnchorEntityType";

/** Config key holding the selected type's input-schema field summaries. */
export const TASK_TYPE_INPUT_FIELDS_CONFIG_KEY = "taskTypeInputFields";

/** Per-field create-task sockets overlay the `input` bag at runtime. */
export const TASK_INPUT_SOCKET_PREFIX = "taskInput.";

export type TaskTypeInputFieldConfig = {
  fieldName: string;
  fieldType: string;
  label: string;
  isRequired: boolean;
  isMultiple: boolean;
  /** Set on an `entity_reference` field so its socket is typed to that entity. */
  referencedEntityType?: string | null;
};

const TaskTypeInputFieldConfigSchema = z.object({
  fieldName: z.string().min(1),
  fieldType: z.string(),
  label: z.string(),
  isRequired: z.boolean(),
  isMultiple: z.boolean(),
  referencedEntityType: z.string().nullable().optional(),
});

const TASK_CREATE_STEP_TYPE_SET: ReadonlySet<string> = new Set(
  TASK_CREATE_STEP_TYPES,
);

export function isTaskCreateStepType(
  stepType: string,
): stepType is TaskCreateStepType {
  return TASK_CREATE_STEP_TYPE_SET.has(stepType);
}

const STEP_SCHEMAS = {
  create_task: {
    input: createTaskStep.inputSchema,
    output: createTaskStep.outputSchema,
  },
  create_typed_task: {
    input: createTypedTaskStep.inputSchema,
    output: createTypedTaskStep.outputSchema,
  },
  generate_task: {
    input: generateTaskStep.inputSchema,
    output: generateTaskStep.outputSchema,
  },
  create_subtask: {
    input: createSubtaskStep.inputSchema,
    output: createSubtaskStep.outputSchema,
  },
} as const;

/**
 * When a type declares an anchor, the typed entity socket is the one the
 * canvas should require. Scalar type/id pairs and the duplicate untyped
 * entity socket are hidden so the node has a single required handle.
 */
const HIDDEN_SOCKET_IDS_WHEN_ANCHORED: Record<
  TaskCreateStepType,
  ReadonlySet<string>
> = {
  create_task: new Set(["sourceEntityType", "sourceEntityId", "anchorEntity"]),
  create_typed_task: new Set([
    "sourceEntityType",
    "sourceEntityId",
    "anchorEntity",
  ]),
  generate_task: new Set(["anchorEntity"]),
  create_subtask: new Set(["anchorEntityType", "anchorEntityId"]),
};

const PRIMARY_ENTITY_SOCKET_ID: Record<TaskCreateStepType, string> = {
  create_task: "sourceEntity",
  create_typed_task: "sourceEntity",
  generate_task: "sourceEntity",
  create_subtask: "anchorEntity",
};

const DeclaredAnchorEntityTypeSchema = z.nativeEnum(SystemEntity);

const TASK_INPUT_SOCKET_TYPE_BY_FIELD: Record<string, SocketType> = {
  string: "string",
  text: "string",
  url: "string",
  enum: "string",
  time: "string",
  integer: "number",
  numeric: "number",
  float: "number",
  currency: "number",
  boolean: "boolean",
  date: "date",
  datetime: "date",
  file: "json",
  image: "json",
  json: "json",
  object: "json",
  geospatial: "json",
  unit: "json",
  range: "json",
  array: "array<json>",
  document: `entity_reference:${SystemEntity.CORE_DOCUMENT}` as SocketType,
};

function arrayWrapSocketType(inner: SocketType): SocketType {
  if (inner.startsWith("entity_reference")) {
    return `array<${inner}>` as SocketType;
  }
  switch (inner) {
    case "string":
      return "array<string>";
    case "number":
      return "array<number>";
    case "boolean":
      return "array<boolean>";
    case "uuid":
      return "array<uuid>";
    default:
      return "array<json>";
  }
}

export function socketTypeForTaskInputField(
  field: Pick<
    TaskTypeInputFieldConfig,
    "fieldType" | "isMultiple" | "referencedEntityType"
  >,
): SocketType {
  if (field.fieldType === "array") {
    return "array<json>";
  }
  const inner = taskInputScalarSocketType(field);
  return field.isMultiple ? arrayWrapSocketType(inner) : inner;
}

/**
 * An `entity_reference` field carries `{ entityType, entityId }`, the same
 * shape an entity socket wires. Typing the socket to the referenced entity
 * lets the canvas connect a typed upstream reference and reject the rest.
 */
function taskInputScalarSocketType(
  field: Pick<TaskTypeInputFieldConfig, "fieldType" | "referencedEntityType">,
): SocketType {
  if (field.fieldType === "entity_reference") {
    return field.referencedEntityType
      ? (`entity_reference:${field.referencedEntityType}` as SocketType)
      : "entity_reference";
  }
  return TASK_INPUT_SOCKET_TYPE_BY_FIELD[field.fieldType] ?? "json";
}

/**
 * The selected type's input-schema fields, as copied onto step config by the
 * task-type picker. Empty when the step has no type, the type has no schema,
 * or the value is malformed.
 */
export function readTaskTypeInputFields(
  config: Record<string, unknown>,
): TaskTypeInputFieldConfig[] {
  const parsed = z
    .array(TaskTypeInputFieldConfigSchema)
    .safeParse(config[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY]);
  return parsed.success ? parsed.data : [];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Merge the optional `input` bag with per-field `taskInput.<name>` sockets.
 * Field sockets win. Returns `undefined` when nothing was supplied so untyped
 * creates do not send an empty bag (the service rejects input without a schema).
 */
export function assembleTaskCreateInput(
  input: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const bag = isPlainObject(input.input) ? { ...input.input } : {};
  for (const [key, value] of Object.entries(input)) {
    if (!key.startsWith(TASK_INPUT_SOCKET_PREFIX) || value === undefined) {
      continue;
    }
    const fieldName = key.slice(TASK_INPUT_SOCKET_PREFIX.length);
    if (!fieldName) continue;
    bag[fieldName] = value;
  }
  return Object.keys(bag).length > 0 ? bag : undefined;
}

function applyTaskInputFieldSockets(
  sockets: DynamicSocketDefinition[],
  fields: readonly TaskTypeInputFieldConfig[],
): DynamicSocketDefinition[] {
  if (fields.length === 0) return sockets;
  const withoutBag = sockets.filter((socket) => socket.id !== "input");
  const fieldSockets = fields.map((field) => ({
    id: `${TASK_INPUT_SOCKET_PREFIX}${field.fieldName}`,
    name: field.label || field.fieldName,
    type: socketTypeForTaskInputField(field),
    required: field.isRequired,
    description: `${field.isRequired ? "Required" : "Optional"} create-time input for this task type: ${field.label || field.fieldName}`,
  }));
  return [...withoutBag, ...fieldSockets];
}

/**
 * Live task-type metadata the save and activation paths resolve from the
 * database. Socket generation itself stays sync; callers overlay this onto
 * config first so a client that omitted or stale-copied `taskTypeInputFields`
 * still gets the type's required sockets.
 */
export type ResolvedTypedTaskSocketConfig = {
  anchorEntityType: string | null;
  inputFields: readonly TaskTypeInputFieldConfig[];
};

/**
 * Replace the client-copied anchor and input-field metadata with the live
 * type. An empty field list clears a stale copy, so a type that no longer
 * has a schema stops requiring sockets that no longer exist.
 */
export function configWithResolvedTypedTaskFields(
  config: Record<string, unknown>,
  resolved: ResolvedTypedTaskSocketConfig,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...config };
  delete next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY];
  delete next[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY];
  if (resolved.anchorEntityType) {
    next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY] = resolved.anchorEntityType;
  }
  if (resolved.inputFields.length > 0) {
    next[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY] = resolved.inputFields;
  }
  return next;
}

/**
 * Config socket generation should see for this step. Only `create_typed_task`
 * locks the type, so only that step replaces client field metadata.
 */
export function configForTypedTaskCreateSockets(
  stepType: string,
  config: Record<string, unknown>,
  resolved: ResolvedTypedTaskSocketConfig | undefined,
): Record<string, unknown> {
  if (stepType !== "create_typed_task" || !resolved) return config;
  return configWithResolvedTypedTaskFields(config, resolved);
}

export type PersistedTypedTaskSocket = {
  id: string;
  expression?: string;
  manualOverride?: boolean;
};

/**
 * Input sockets for a Create Typed Task step after the live type is known.
 * Expressions already stored on a socket id are kept, so a manual override
 * still counts as connected when activation re-checks the graph.
 */
export function typedTaskCreateInputSocketsFromResolved(args: {
  config: Record<string, unknown>;
  resolved: ResolvedTypedTaskSocketConfig;
  persistedInputs?: readonly PersistedTypedTaskSocket[];
}): Array<
  DynamicSocketDefinition & { expression?: string; manualOverride?: boolean }
> {
  const generated = generateTaskCreateInputSockets(
    "create_typed_task",
    configWithResolvedTypedTaskFields(args.config, args.resolved),
  );
  const persistedById = new Map(
    (args.persistedInputs ?? []).map((socket) => [socket.id, socket]),
  );
  return generated.map((socket) => {
    const persisted = persistedById.get(socket.id);
    if (!persisted?.expression) return socket;
    return {
      ...socket,
      expression: persisted.expression,
      manualOverride: persisted.manualOverride,
    };
  });
}

/**
 * The anchor entity type the selected task type declares, as copied onto step
 * config by the task-type picker. `undefined` when the step has no type, the
 * type is unanchored, or the value is malformed.
 */
export function readDeclaredTaskTypeAnchorEntityType(
  config: Record<string, unknown>,
): SystemEntity | undefined {
  const parsed = DeclaredAnchorEntityTypeSchema.safeParse(
    config[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY],
  );
  return parsed.success ? parsed.data : undefined;
}

function toDynamicSocket(socket: {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}): DynamicSocketDefinition {
  return {
    id: socket.id,
    name: socket.name,
    type: socket.type,
    required: socket.required,
    description: socket.description,
  };
}

/**
 * Input sockets for create_task / create_typed_task / generate_task / create_subtask.
 *
 * With no declared anchor, this is the static Zod input schema (optional
 * generic source/anchor). When the selected type declares an
 * `anchorEntityType` and `taskTypeKey` cannot be wired at runtime, the
 * primary entity socket becomes required and typed as
 * `entity_reference:<that type>` so the canvas (and graph validation)
 * enforce the type's source-entity constraint.
 *
 * `create_task` and `create_subtask` resolve the runtime type as
 * `input.taskTypeKey ?? config.taskTypeKey`, so a wired override can pick a
 * different anchor and a different input schema than config. In that case we
 * keep the generic entity sockets and the optional `input` bag instead of
 * enforcing the configured type's fields.
 *
 * `create_typed_task` has no `taskTypeKey` input. The configured type is the
 * type that runs, so its anchor and input-schema fields are always enforced.
 */
export function generateTaskCreateInputSockets(
  stepType: TaskCreateStepType,
  config: Record<string, unknown>,
  options?: { taskTypeKeyCanBeWired?: boolean },
): DynamicSocketDefinition[] {
  const sockets = generateSocketsFromSchema(
    STEP_SCHEMAS[stepType].input,
    "input",
  ).map(toDynamicSocket);

  const taskTypeKeyCanBeWired =
    options?.taskTypeKeyCanBeWired ??
    (stepType === "create_task" || stepType === "create_subtask");
  const declaredAnchor = readDeclaredTaskTypeAnchorEntityType(config);
  const withAnchor =
    !declaredAnchor || taskTypeKeyCanBeWired
      ? sockets
      : rewriteAnchoredEntitySockets(stepType, declaredAnchor, sockets);

  if (taskTypeKeyCanBeWired) return withAnchor;
  return applyTaskInputFieldSockets(
    withAnchor,
    readTaskTypeInputFields(config),
  );
}

function rewriteAnchoredEntitySockets(
  stepType: TaskCreateStepType,
  declaredAnchor: SystemEntity,
  sockets: DynamicSocketDefinition[],
): DynamicSocketDefinition[] {
  const hidden = HIDDEN_SOCKET_IDS_WHEN_ANCHORED[stepType];
  const primaryId = PRIMARY_ENTITY_SOCKET_ID[stepType];
  const label = SYSTEM_ENTITY_LABELS[declaredAnchor] ?? declaredAnchor;
  const typedType = `entity_reference:${declaredAnchor}` as SocketType;

  return sockets.flatMap((socket) => {
    if (hidden.has(socket.id)) {
      return [];
    }
    if (socket.id !== primaryId) {
      return [socket];
    }
    return [
      {
        ...socket,
        name: label,
        type: typedType,
        required: true,
        description: `Required ${label} for this task type`,
      },
    ];
  });
}

/**
 * Output sockets for the create-family steps. Config does not change them;
 * this exists so editor reconciliation can replace inputs without wiping
 * the static `task` / `taskId` / `metadata` / `created` outputs.
 */
export function generateTaskCreateOutputSockets(
  stepType: TaskCreateStepType,
): DynamicSocketDefinition[] {
  return generateSocketsFromSchema(STEP_SCHEMAS[stepType].output, "output").map(
    toDynamicSocket,
  );
}
